import * as React from "react";

/**
 * Distance below the scroll container's top edge at which a task is considered
 * "the one the reader is looking at". A task becomes active once its anchor has
 * scrolled up past this line, so the highlight tracks the content near the top
 * of the viewport rather than flipping the moment the next task peeks in at the
 * bottom.
 */
const TRIGGER_OFFSET_PX = 96;

/**
 * Scroll-spy that reports which task the reader is currently looking at.
 *
 * Anchors in the conversation carry `data-task-id` (message articles tagged via
 * `taskIdForMessageIndex`, and concluded task-section headers). On each scroll
 * we walk those anchors in document order — which equals visual top-to-bottom
 * order in the single-column transcript — and take the last one whose top has
 * crossed the trigger line. The result drives the highlight in the task panel.
 *
 * @param scrollContainerRef - the scrollable transcript viewport
 * @param revision - bump to recompute when the anchor set changes (new
 *   messages streamed in, a section expanded/collapsed); scrolling recomputes
 *   on its own, this only covers content changes at a fixed scroll position.
 */
export function useActiveTaskOnScroll(
  scrollContainerRef: React.RefObject<HTMLElement>,
  revision: number,
): number | undefined {
  const [activeTaskId, setActiveTaskId] = React.useState<number | undefined>(
    undefined,
  );

  React.useEffect(
    function trackActiveTask() {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }

      let frame = 0;

      const compute = () => {
        frame = 0;
        const triggerY =
          container.getBoundingClientRect().top + TRIGGER_OFFSET_PX;
        const anchors =
          container.querySelectorAll<HTMLElement>("[data-task-id]");

        let active: number | undefined;
        for (const anchor of anchors) {
          if (anchor.getBoundingClientRect().top <= triggerY) {
            const id = Number(anchor.dataset.taskId);
            if (!Number.isNaN(id)) {
              active = id;
            }
          } else {
            // Anchors are in document (visual) order; once one sits below the
            // trigger line, so do all that follow.
            break;
          }
        }

        setActiveTaskId((prev) => (prev === active ? prev : active));
      };

      const onScroll = () => {
        if (frame === 0) {
          frame = window.requestAnimationFrame(compute);
        }
      };

      compute();
      container.addEventListener("scroll", onScroll, { passive: true });

      return () => {
        container.removeEventListener("scroll", onScroll);
        if (frame !== 0) {
          window.cancelAnimationFrame(frame);
        }
      };
    },
    [scrollContainerRef, revision],
  );

  return activeTaskId;
}
